function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function roundScore(value) {
    return Math.round(clamp(value, 0, 100));
}

function getFlowerSize(flower) {
    return flower && flower.params && typeof flower.params.size === 'number'
        ? flower.params.size
        : 1;
}

function getFlowerPosition(flower) {
    const position = flower && flower.position ? flower.position : {};
    return {
        x: typeof position.x === 'number' ? position.x : 0,
        y: typeof position.y === 'number' ? position.y : 0,
        z: typeof position.z === 'number' ? position.z : 0,
    };
}

function getStats(flowers) {
    const positions = flowers.map(getFlowerPosition);
    const sizes = flowers.map(getFlowerSize);
    const xs = positions.map(item => item.x);
    const ys = positions.map(item => item.y);
    const zs = positions.map(item => item.z);
    const maxY = Math.max(...ys, 0);
    const minY = Math.min(...ys, 0);
    const maxX = Math.max(...xs, 0);
    const minX = Math.min(...xs, 0);
    const maxZ = Math.max(...zs, 0);
    const minZ = Math.min(...zs, 0);
    const maxSize = Math.max(...sizes, 1);
    const avgSize = sizes.reduce((sum, item) => sum + item, 0) / Math.max(sizes.length, 1);

    return {
        positions,
        sizes,
        maxY,
        minY,
        heightRange: maxY - minY,
        maxX,
        minX,
        xRange: maxX - minX,
        maxZ,
        minZ,
        zRange: maxZ - minZ,
        maxSize,
        avgSize,
        centerX: positions.reduce((sum, item, index) => sum + item.x * sizes[index], 0)
            / Math.max(sizes.reduce((sum, item) => sum + item, 0), 1),
    };
}

function getContainerSize(container) {
    if (!container) {
        return {
            height: 0,
            width: 0,
        };
    }
    const size = container.params && typeof container.params.size === 'number'
        ? container.params.size
        : 1;
    return {
        height: (container.height || 100) * size,
        width: ((container.radius || 70) * 2) * size,
    };
}

function getAverageNearestDistance(positions) {
    if (positions.length < 2) {
        return 0;
    }
    const nearestDistances = positions.map((source, sourceIndex) => {
        let nearest = Infinity;
        positions.forEach((target, targetIndex) => {
            if (sourceIndex === targetIndex) {
                return;
            }
            const dx = source.x - target.x;
            const dy = (source.y - target.y) * 0.35;
            const dz = source.z - target.z;
            nearest = Math.min(nearest, Math.sqrt(dx * dx + dy * dy + dz * dz));
        });
        return nearest;
    });
    return nearestDistances.reduce((sum, item) => sum + item, 0) / nearestDistances.length;
}

function makeMetric(key, name, score, comment) {
    return {
        key,
        name,
        score: roundScore(score),
        comment,
    };
}

function scoreHeightRatio(flowers, container, stats) {
    if (!flowers.length) {
        return makeMetric('heightRatio', '高度比例', 0, '还没有花材，暂时无法判断高度比例。');
    }
    const containerSize = getContainerSize(container);
    if (!containerSize.height) {
        return makeMetric('heightRatio', '高度比例', 58, '缺少器皿，花材高度缺少参照。');
    }
    const ratio = stats.maxY / containerSize.height;
    const distance = Math.abs(ratio - 2.35);
    const score = 100 - distance * 34;
    const comment = ratio < 1.5
        ? '主枝略低，作品气势可以再拉开。'
        : ratio > 3.5
            ? '主枝偏高，器皿压不住整体高度。'
            : '花材高度和器皿比例比较协调。';
    return makeMetric('heightRatio', '高度比例', score, comment);
}

function scoreBalance(flowers, stats) {
    if (flowers.length < 2) {
        return makeMetric('balance', '左右平衡', 60, '花材较少，左右重心还不够稳定。');
    }
    const baseRange = Math.max(stats.xRange, 70);
    const offsetRate = Math.abs(stats.centerX) / baseRange;
    const score = 100 - offsetRate * 165;
    const comment = offsetRate > 0.35
        ? stats.centerX > 0
            ? '整体重心偏右，可以在左侧补充中低位花材。'
            : '整体重心偏左，可以在右侧补充中低位花材。'
        : '左右重心比较稳定。';
    return makeMetric('balance', '左右平衡', score, comment);
}

function scoreLayering(flowers, stats) {
    if (flowers.length < 3) {
        return makeMetric('layering', '层次变化', 55, '花材数量偏少，高低和前后层次还不明显。');
    }
    const heightScore = clamp(stats.heightRange / 160 * 100, 35, 100);
    const depthScore = clamp(stats.zRange / 90 * 100, 30, 100);
    const sizeScore = clamp((stats.maxSize - Math.min(...stats.sizes)) / 0.6 * 100, 35, 100);
    const score = heightScore * 0.45 + depthScore * 0.3 + sizeScore * 0.25;
    const comment = score < 70
        ? '高低、前后或大小变化还可以再拉开。'
        : '作品有一定高低和前后层次。';
    return makeMetric('layering', '层次变化', score, comment);
}

function scoreDensity(flowers, stats) {
    if (!flowers.length) {
        return makeMetric('density', '疏密关系', 0, '还没有花材，暂时无法判断疏密。');
    }
    if (flowers.length === 1) {
        return makeMetric('density', '疏密关系', 52, '单枝作品比较单薄，可以增加辅助花材。');
    }
    const nearest = getAverageNearestDistance(stats.positions);
    const ideal = 85;
    const score = 100 - Math.abs(nearest - ideal) * 0.85;
    const comment = nearest < 45
        ? '花材靠得较近，局部会显得拥挤。'
        : nearest > 150
            ? '花材分布偏散，整体关系可以再收拢。'
            : '花材疏密关系比较自然。';
    return makeMetric('density', '疏密关系', score, comment);
}

function scoreFocus(flowers, stats) {
    if (!flowers.length) {
        return makeMetric('focus', '主次关系', 0, '还没有花材，暂时无法判断主次。');
    }
    const dominance = stats.maxSize / Math.max(stats.avgSize, 0.1);
    const highestBonus = stats.heightRange > 120 ? 12 : 0;
    const countBonus = flowers.length >= 3 ? 8 : 0;
    const score = 55 + (dominance - 1) * 75 + highestBonus + countBonus;
    const comment = score < 72
        ? '主花不够突出，可以放大或抬高一枝作为视觉焦点。'
        : '作品已经有较明确的视觉焦点。';
    return makeMetric('focus', '主次关系', score, comment);
}

function scoreContainerHarmony(flowers, container, stats) {
    if (!container) {
        return makeMetric('containerHarmony', '器皿协调', 48, '缺少器皿，作品落点不够完整。');
    }
    if (!flowers.length) {
        return makeMetric('containerHarmony', '器皿协调', 58, '已有器皿，但还需要花材形成完整作品。');
    }
    const containerSize = getContainerSize(container);
    const widthRatio = Math.max(stats.xRange, 30) / Math.max(containerSize.width, 1);
    const heightRatio = stats.maxY / Math.max(containerSize.height, 1);
    const widthScore = 100 - Math.abs(widthRatio - 0.95) * 45;
    const heightScore = 100 - Math.abs(heightRatio - 2.35) * 30;
    const score = widthScore * 0.45 + heightScore * 0.55;
    const comment = score < 72
        ? '器皿和花材体量还可以再调整，让上下比例更稳。'
        : '器皿和花材体量关系比较协调。';
    return makeMetric('containerHarmony', '器皿协调', score, comment);
}

function getSummary(totalScore) {
    if (totalScore >= 85) {
        return '整体完成度较高，结构稳定，已经有较好的展示效果。';
    }
    if (totalScore >= 70) {
        return '作品基础关系成立，继续微调层次和重心会更完整。';
    }
    if (totalScore >= 55) {
        return '作品已有雏形，但比例、层次或主次关系还需要加强。';
    }
    return '作品信息还不够完整，建议先补充花材、器皿和基本层次。';
}

function getSuggestions(metrics) {
    const weakMetrics = metrics
        .filter(item => item.score < 75)
        .sort((a, b) => a.score - b.score)
        .slice(0, 3);
    if (!weakMetrics.length) {
        return ['整体关系较完整，可以尝试从色彩和主题表达上继续提升。'];
    }
    return weakMetrics.map(item => item.comment);
}

function scoreArtwork(jsonData = {}) {
    const objects = jsonData.objects || {};
    const flowers = objects.flowers || [];
    const container = objects.container || (objects.containers && objects.containers[0]) || null;
    const stats = getStats(flowers);
    const metrics = [
        scoreHeightRatio(flowers, container, stats),
        scoreBalance(flowers, stats),
        scoreLayering(flowers, stats),
        scoreDensity(flowers, stats),
        scoreFocus(flowers, stats),
        scoreContainerHarmony(flowers, container, stats),
    ];
    const totalScore = roundScore(
        metrics.reduce((sum, item) => sum + item.score, 0) / metrics.length
    );
    return {
        totalScore,
        metrics,
        suggestions: getSuggestions(metrics),
        summary: getSummary(totalScore),
    };
}

module.exports = {
    scoreArtwork,
};
